import { Check, Palette, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDensity } from "@/hooks/use-density";
import { useUpdateUserDisplay } from "@/hooks/use-user-display";
import { LOSS_RATIO_DEFAULTS, type LossRatioAppearance } from "@/lib/appearance";
import {
  STATUS_DEFINITIONS,
  STATUS_GROUP_LABELS,
  STATUS_KEYS,
  type StatusGroup,
  type StatusKey,
} from "@/lib/status-colors";
import { useAppearance } from "./AppearanceProvider";

// MEMIC defaults shown in the swatch when the user hasn't picked a custom
// color. The native <input type="color"> needs a concrete hex even when we
// store null, so we fall back to the brand blue / off-white from globals.css.
const DEFAULT_ACCENT = "#005a9b";
const DEFAULT_BACKGROUND = "#f8fafb";

export function AppearancePicker() {
  const {
    appearance,
    setAccent,
    setBackground,
    setStatusColor,
    setLossRatio,
    resetLossRatio,
    reset,
  } = useAppearance();
  // Persist accent + background to UserDisplay so they ride across devices
  // for this user. Status colors are localStorage-only (see comment in
  // src/lib/appearance.ts) so the mutate call only forwards the two fields
  // the server knows about.
  const { mutate: persist } = useUpdateUserDisplay();

  const accentValue = appearance.accent ?? DEFAULT_ACCENT;
  const backgroundValue = appearance.background ?? DEFAULT_BACKGROUND;
  const hasCustomBase = appearance.accent !== null || appearance.background !== null;
  const hasCustomStatus = Object.keys(appearance.statusColors).length > 0;
  const hasCustomLossRatio =
    appearance.lossRatio.lowThreshold !== LOSS_RATIO_DEFAULTS.lowThreshold ||
    appearance.lossRatio.highThreshold !== LOSS_RATIO_DEFAULTS.highThreshold ||
    appearance.lossRatio.lowColor !== LOSS_RATIO_DEFAULTS.lowColor ||
    appearance.lossRatio.midColor !== LOSS_RATIO_DEFAULTS.midColor ||
    appearance.lossRatio.highColor !== LOSS_RATIO_DEFAULTS.highColor;
  const hasAnyCustom = hasCustomBase || hasCustomStatus || hasCustomLossRatio;

  function handleSetAccent(value: string | null) {
    setAccent(value);
    persist({ appearanceAccent: value });
  }
  function handleSetBackground(value: string | null) {
    setBackground(value);
    persist({ appearanceBackground: value });
  }
  function handleReset() {
    reset();
    persist({ appearanceAccent: null, appearanceBackground: null });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Customize colors">
          <Palette className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[85vh] w-[440px] overflow-y-auto">
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className="space-y-2 px-2 py-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Density
            </span>
            <span className="text-[10px] text-muted-foreground/80">
              Controls how tightly rows stack on every table
            </span>
          </div>
          <DensitySegmented />
        </div>

        <DropdownMenuSeparator />

        <div className="space-y-3 px-2 py-2">
          <ColorRow
            label="Header & accents"
            value={accentValue}
            isCustom={appearance.accent !== null}
            onChange={handleSetAccent}
            onClear={() => handleSetAccent(null)}
          />
          <ColorRow
            label="App background"
            value={backgroundValue}
            isCustom={appearance.background !== null}
            onChange={handleSetBackground}
            onClear={() => handleSetBackground(null)}
          />
        </div>

        <DropdownMenuSeparator />
        {/* Two-column layout: Policy (status + loss-ratio) on the left,
            Claim (status) and Other (restricted) on the right.
            Separation via vertical border between columns + horizontal
            borders above LOSS RATIO and OTHER. */}
        <div className="grid grid-cols-2 px-2 py-2">
          <div className="border-r pr-3">
            <StatusColumn
              group="policy"
              statusColors={appearance.statusColors}
              onChangeStatus={setStatusColor}
              footer={
                <div className="mt-3 space-y-1.5 border-t pt-3">
                  <div className="flex items-center justify-between gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span>Loss ratio</span>
                    <button
                      type="button"
                      onClick={resetLossRatio}
                      disabled={!hasCustomLossRatio}
                      className="font-normal text-[10px] normal-case tracking-normal hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Reset
                    </button>
                  </div>
                  <LossRatioRows lossRatio={appearance.lossRatio} onChange={setLossRatio} />
                </div>
              }
            />
          </div>
          <div className="pl-3">
            <StatusColumn
              group="claim"
              statusColors={appearance.statusColors}
              onChangeStatus={setStatusColor}
              padRows={4}
            />
            <div className="mt-3 border-t pt-3">
              <StatusColumn
                group="other"
                statusColors={appearance.statusColors}
                onChangeStatus={setStatusColor}
              />
            </div>
            <div className="mt-3 border-t pt-3">
              <StatusColumn
                group="announcement"
                statusColors={appearance.statusColors}
                onChangeStatus={setStatusColor}
              />
            </div>
          </div>
        </div>

        <DropdownMenuSeparator />
        <div className="px-2 py-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleReset}
            disabled={!hasAnyCustom}
          >
            <RotateCcw className="size-4" />
            Reset all
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const DENSITY_PREVIEW_ROWS = ["a", "b", "c", "d"] as const;

function DensityPreview({ rowCount, gapClass }: { rowCount: 3 | 4; gapClass: string }) {
  return (
    <div className={`flex flex-col ${gapClass}`}>
      {DENSITY_PREVIEW_ROWS.slice(0, rowCount).map((key) => (
        <div
          key={key}
          className="h-1 w-full rounded-sm bg-muted-foreground/60 dark:bg-muted-foreground"
        />
      ))}
    </div>
  );
}

function DensitySegmented() {
  const { density, setDensity } = useDensity();
  return (
    <div className="grid grid-cols-2 gap-2">
      <DensityCard
        label="Comfortable"
        description="Roomier rows — fewer rows visible, but each one is easier to scan on a big screen."
        active={density === "comfortable"}
        onClick={() => setDensity("comfortable")}
      >
        <DensityPreview rowCount={3} gapClass="gap-1.5" />
      </DensityCard>
      <DensityCard
        label="Compact"
        description="Tighter rows — more rows fit on screen at once. Better for long lists."
        active={density === "compact"}
        onClick={() => setDensity("compact")}
      >
        <DensityPreview rowCount={4} gapClass="gap-[3px]" />
      </DensityCard>
    </div>
  );
}

function DensityCard({
  label,
  description,
  active,
  onClick,
  children,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={description}
      className={`group flex flex-col gap-2 rounded-md border-2 px-3 py-2.5 text-left transition-colors ${
        active
          ? "border-primary bg-primary/5"
          : "border-border bg-muted/30 hover:border-muted-foreground/40 hover:bg-muted/60"
      }`}
    >
      {children}
      <div className="flex items-center justify-between gap-2">
        <span
          className={`text-xs font-medium ${active ? "text-foreground" : "text-muted-foreground"}`}
        >
          {label}
        </span>
        {active ? <Check className="size-3.5 text-primary" aria-hidden="true" /> : null}
      </div>
    </button>
  );
}

// Curated palette for the SwatchPicker. Spans the full hue circle plus a
// neutral row. Chosen to look good as both background tints (light, ~150–250
// luminance) and accent fills (saturated). 24 colors keeps the popover
// compact (8 per row × 3 rows). Hex codes match Tailwind 500/300 stops where
// possible so picks read as familiar UI colors rather than arbitrary HSL.
const PALETTE: string[] = [
  // Reds / oranges / yellows
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#eab308",
  // Greens / teals
  "#84cc16",
  "#22c55e",
  "#10b981",
  "#14b8a6",
  // Blues / purples
  "#06b6d4",
  "#0ea5e9",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#a855f7",
  "#d946ef",
  "#ec4899",
  // Pastels for backgrounds
  "#fecaca",
  "#fed7aa",
  "#fef08a",
  "#bbf7d0",
  // Neutrals
  "#f8fafc",
  "#cbd5e1",
  "#475569",
  "#0f172a",
];

function SwatchPicker({
  value,
  ariaLabel,
  onChange,
}: {
  value: string;
  ariaLabel: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Local hex input — lets the user type any color without immediately
  // committing partial values like "#1" on the way to "#1abc9c". Commits
  // on blur or Enter, snaps back to the saved value if invalid.
  const [hexDraft, setHexDraft] = useState(value);
  useEffect(() => setHexDraft(value), [value]);

  function commitHex(raw: string) {
    const normalized = raw.startsWith("#") ? raw : `#${raw}`;
    if (/^#[0-9a-f]{6}$/i.test(normalized)) {
      onChange(normalized.toLowerCase());
    } else {
      setHexDraft(value);
    }
  }

  // Custom popover-based picker — does NOT use <input type="color">.
  // Reason: Safari shows a quick-pick color grid for invisible color
  // inputs but does NOT fire input/change events from that grid (only
  // from the full "Show Colors..." panel). A custom popover with a real
  // color wheel (react-colorful) + hex input + preset swatches sidesteps
  // that entirely and looks consistent across browsers.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          style={{ backgroundColor: value }}
          className="inline-block h-6 w-8 cursor-pointer rounded border border-foreground/30 ring-offset-1 transition-shadow hover:ring-2 hover:ring-primary/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:outline-none"
        />
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-auto space-y-3 p-3">
        <HexColorPicker color={value} onChange={onChange} />
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Hex
          </span>
          <Input
            value={hexDraft}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={(e) => commitHex(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitHex(e.currentTarget.value);
              }
            }}
            className="h-7 flex-1 font-mono text-xs"
            spellCheck={false}
            aria-label="Hex color value"
          />
        </div>
        <div>
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Presets
          </div>
          <div className="grid grid-cols-8 gap-1">
            {PALETTE.map((hex) => {
              const isSelected = hex.toLowerCase() === value.toLowerCase();
              return (
                <button
                  key={hex}
                  type="button"
                  aria-label={`Pick ${hex}`}
                  onClick={() => {
                    onChange(hex);
                    setOpen(false);
                  }}
                  style={{ backgroundColor: hex }}
                  className={`relative h-6 w-6 rounded border transition-shadow hover:ring-2 hover:ring-primary/40 ${
                    isSelected ? "border-foreground ring-2 ring-primary/60" : "border-foreground/20"
                  }`}
                >
                  {isSelected ? (
                    <Check
                      className="absolute inset-0 m-auto size-3.5 drop-shadow-sm"
                      style={{ color: deriveForegroundColor(hex) }}
                      aria-hidden="true"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Local foreground-color helper for the check icon — same WCAG logic as
// the StatusBadge fg derivation, inlined here so this component stays
// self-contained.
function deriveForegroundColor(bgHex: string): string {
  const m = bgHex.trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return "#0f172a";
  const n = Number.parseInt(m[1] ?? "0", 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.55 ? "#1e293b" : "#f8fafc";
}

type ColorRowProps = {
  label: string;
  value: string;
  isCustom: boolean;
  onChange: (next: string) => void;
  onClear: () => void;
};

function ColorRow({ label, value, isCustom, onChange, onClear }: ColorRowProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <SwatchPicker value={value} onChange={onChange} ariaLabel={`Pick ${label}`} />
        <button
          type="button"
          onClick={onClear}
          disabled={!isCustom}
          className="text-muted-foreground text-xs hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reset
        </button>
      </div>
    </div>
  );
}

type StatusColumnProps = {
  group: StatusGroup;
  statusColors: Partial<Record<StatusKey, string>>;
  onChangeStatus: (key: StatusKey, value: string | null) => void;
  footer?: React.ReactNode;
  // Pad the status block with invisible rows up to this count so columns
  // with different numbers of statuses still align on their dividers.
  // Used so Claim's 3 status rows occupy the same vertical space as
  // Policy's 4, lining OTHER up with LOSS RATIO.
  padRows?: number;
};

function StatusColumn({
  group,
  statusColors,
  onChangeStatus,
  footer,
  padRows = 0,
}: StatusColumnProps) {
  const keys = STATUS_KEYS.filter((k) => STATUS_DEFINITIONS[k].group === group);
  if (keys.length === 0) return null;
  // "Other" is a one-off bucket (just Restricted) — skipping the
  // redundant "status" sub-label there keeps the column header readable.
  const showStatusSubheading = group !== "other";
  // Unique tokens for the placeholder rows so React can key them without
  // relying on array index (biome's noArrayIndexKey rule).
  const padTokens = Array.from(
    { length: Math.max(0, padRows - keys.length) },
    (_, i) => `pad-${group}-${keys.length + i}`,
  );
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {STATUS_GROUP_LABELS[group]}
      </div>
      {showStatusSubheading ? (
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70">status</div>
      ) : null}
      <div className="space-y-1.5">
        {keys.map((key) => (
          <StatusRow
            key={key}
            statusKey={key}
            override={statusColors[key] ?? null}
            onChange={(hex) => onChangeStatus(key, hex)}
          />
        ))}
        {padTokens.map((token) => (
          <div key={token} aria-hidden="true" className="h-6" />
        ))}
      </div>
      {footer}
    </div>
  );
}

type StatusRowProps = {
  statusKey: StatusKey;
  override: string | null;
  onChange: (next: string | null) => void;
};

function StatusRow({ statusKey, override, onChange }: StatusRowProps) {
  const def = STATUS_DEFINITIONS[statusKey];
  const value = override ?? def.defaultBg;
  const isCustom = override !== null;
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="truncate text-xs">{def.shortLabel}</span>
      <div className="flex items-center gap-1.5">
        <SwatchPicker value={value} onChange={onChange} ariaLabel={`Pick ${def.label} color`} />
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={!isCustom}
          className="text-[10px] text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reset
        </button>
      </div>
    </div>
  );
}

type LossRatioRowsProps = {
  lossRatio: LossRatioAppearance;
  onChange: (patch: Partial<LossRatioAppearance>) => void;
};

function LossRatioRows({ lossRatio, onChange }: LossRatioRowsProps) {
  // Thresholds bound to local state so the user can type a partial value
  // (e.g. delete to "" before retyping) without the input fighting them.
  // We only commit on blur or a valid full number.
  function commitLow(next: number) {
    // Guard ordering: low must stay below high. If the user types a value at
    // or past high, snap to high - 1 so the bands don't collapse.
    const clamped = Math.min(Math.max(0, next), lossRatio.highThreshold - 1);
    if (clamped !== lossRatio.lowThreshold) onChange({ lowThreshold: clamped });
  }
  function commitHigh(next: number) {
    const clamped = Math.max(next, lossRatio.lowThreshold + 1);
    if (clamped !== lossRatio.highThreshold) onChange({ highThreshold: clamped });
  }

  return (
    <div className="space-y-1.5">
      <LossRatioRow
        label="Below"
        suffix="%"
        threshold={lossRatio.lowThreshold}
        onThresholdChange={commitLow}
        color={lossRatio.lowColor}
        onColorChange={(hex) => onChange({ lowColor: hex })}
      />
      <LossRatioRow
        label={`Between ${lossRatio.lowThreshold}% and ${lossRatio.highThreshold}%`}
        color={lossRatio.midColor}
        onColorChange={(hex) => onChange({ midColor: hex })}
      />
      <LossRatioRow
        label="Above"
        suffix="%"
        threshold={lossRatio.highThreshold}
        onThresholdChange={commitHigh}
        color={lossRatio.highColor}
        onColorChange={(hex) => onChange({ highColor: hex })}
      />
    </div>
  );
}

type LossRatioRowProps = {
  label: string;
  color: string;
  onColorChange: (hex: string) => void;
  threshold?: number;
  onThresholdChange?: (next: number) => void;
  suffix?: string;
};

function LossRatioRow({
  label,
  color,
  onColorChange,
  threshold,
  onThresholdChange,
  suffix,
}: LossRatioRowProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1.5 text-xs">
        <span className="truncate">{label}</span>
        {threshold !== undefined && onThresholdChange ? (
          <>
            <input
              type="number"
              min={0}
              max={1000}
              value={threshold}
              onChange={(e) => {
                const parsed = Number.parseInt(e.target.value, 10);
                if (Number.isFinite(parsed)) onThresholdChange(parsed);
              }}
              className="h-6 w-14 rounded border bg-background px-1.5 text-xs tabular-nums"
              aria-label={`${label} threshold`}
            />
            {suffix ? <span className="text-muted-foreground">{suffix}</span> : null}
          </>
        ) : null}
      </div>
      <SwatchPicker
        value={color}
        onChange={onColorChange}
        ariaLabel={`Pick ${label} loss-ratio color`}
      />
    </div>
  );
}
